
import { motion } from "framer-motion";
import {
  FaBatteryFull,
  FaBolt,
  FaCogs,
  FaChartLine,
  FaTools,
} from "react-icons/fa";
import beesVideo from "../assets/video/beesvidieo.mp4";
import "../styles/BatteryStorage.scss";

const BatteryStorage = () => {
  const functionalities = [
    { title: "Ramp Rate Control", icon: <FaChartLine />, desc: "Power Smoothing" },
    { title: "Energy Shifting", icon: <FaBatteryFull />, desc: "Optimize energy usage" },
    { title: "Customizable Power Profiles", icon: <FaCogs />, desc: "Flexible schedules" },
    { title: "Grid Voltage Control", icon: <FaBolt />, desc: "Stabilize grid voltage" },
    { title: "Power Frequency Response", icon: <FaChartLine />, desc: "PFR / P(f)" },
    { title: "Enhanced Frequency Response", icon: <FaBolt />, desc: "EFR & AFRR / SRL" },
  ];

  const products = [
    {
      title: "Semi-Integrated BESS",
      icon: <FaBatteryFull />,
      desc: "Battery rack with auxiliary systems such as cooling, fire protection, lighting and earthing.",
    },
    {
      title: "Container Enclosure Body",
      icon: <FaTools />,
      desc: "Foundation-ready container solution enabling flexible battery rack integration.",
    },
    {
      title: "Fully Integrated BESS",
      icon: <FaCogs />,
      desc: "Plug-and-play system with battery packs, BMS, DC combiners and cooling.",
    },
    {
      title: "EPC Services",
      icon: <FaTools />,
      desc: "End-to-end EPC services from engineering and procurement to commissioning.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const cardHoverVariants = {
    hover: {
      y: -5,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="battery-storage section-padding" id="bess" style={{ background: 'linear-gradient(180deg, #ffffff, #f0f7ff)' }}>
      <div className="container">
        <motion.div
          className="row justify-content-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
        >
          <div className="col-12">
            <motion.header className="text-center mb-5" variants={itemVariants}>
              <h1 className="section-title">BESS Solutions</h1>
              <p className="section-subtitle">
                TLS Energy's Battery Energy Storage Systems offer cutting-edge
                functionalities for optimal energy management
              </p>
            </motion.header>
          </div>
        </motion.div>

        {/* Enhanced Video-Cards Integration Section */}
        <motion.div
          className="video-features-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
        >
          <div className="row align-items-center g-4">
            {/* Video Section with Enhanced Styling */}
            <div className="col-lg-6">
              <motion.div
                className="video-container h-100 w-100"
                variants={itemVariants}
                transition={{ duration: 0.1 }}
              >
                <div className="video-wrapper">
                  <video
                    className="w-100"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                  >
                    <source src={beesVideo} type="video/mp4" />
                  </video>
                </div>
                <motion.div
                  className="video-caption"
                  variants={itemVariants}
                >
                  <h3>Advanced BESS Technology</h3>
                  <p>Experience cutting-edge battery energy storage solutions in action</p>
                </motion.div>
              </motion.div>
            </div>

            {/* Features Cards Section */}
            <div className="col-lg-6">
              <motion.div className="features-content h-100" variants={itemVariants}>
                <div className="features-header mb-4">
                  <motion.h2
                    className="fs-4 "
                    variants={itemVariants}
                  >
                    Key Functionalities
                  </motion.h2>
                </div>

                <div className="features-grid row g-3">
                  {functionalities.map((item, index) => (
                    <div key={index} className="col-md-6">
                      <motion.div
                        className="feature-card-enhanced h-100 p-3"
                        variants={cardHoverVariants}
                        whileHover="hover"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          delay: index * 0.1,
                          duration: 0.5,
                          ease: "easeOut"
                        }}
                      >
                        <div className="feature-content d-flex flex-row align-items-start text-start w-100">
                          <div className="feature-icon-enhanced ps-0 mb-0 me-3">
                            {item.icon}
                          </div>
                          <div className="feature-text">
                            <h4 className="feature-title-enhanced">{item.title}</h4>
                            <p className="feature-desc-enhanced">{item.desc}</p>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <motion.section
          className="products-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
        >
          <div className="row justify-content-center">
            <div className="col-12">
              <motion.header className="text-center mb-5" variants={itemVariants}>
                <h2 className="section-title">BESS Container Products & Services</h2>
                <p className="section-subtitle">
                  Comprehensive solutions tailored to your energy storage needs
                </p>
              </motion.header>
            </div>
          </div>

          <div className="row g-4">
            {products.map((product, index) => (
              <div key={index} className="col-lg-3 col-md-6">
                <motion.div
                  className="product-card h-100"
                  variants={cardHoverVariants}
                  whileHover="hover"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="product-icon">{product.icon}</div>
                  <h4 className="product-title">{product.title}</h4>
                  <p className="product-desc">{product.desc}</p>
                  <motion.span
                    className="learn-more"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    Learn More →
                  </motion.span>
                </motion.div>
              </div>
            ))}
          </div>
        </motion.section>
      </div>
    </section>
  );
};

export default BatteryStorage;
